import { Fragment, useMemo, useState } from "react";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import DataTable from "../../Components/DataTable";
import { useNavigate } from "react-router";
import "sweetalert2/dist/sweetalert2.min.css";
import { FormFilterOrder } from "../../Components/order/FormFilterOrder";
import { getOrderColumns } from "../../Components/order/order.columns";
import { API_BASE_URL } from "../../api/api";
import { PaymentProofModal } from "../../Components/order/PaymentProofModal";
import StatsCards from "../../Components/order/StatsCards";
import { useAdmin } from "../../../context/AdminContext";
import { PageHeader } from "../../Components/common/PageHeader";

export const OrderList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [wompiReference, setWompiReference] = useState("");
  const [filters, setFilters] = useState({ status: "", country: "", user: "", payment_method: '', wompi_reference: '' });
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [proofOrder, setProofOrder] = useState(null);

  const { getData } = useDataFetch();
  const { admin } = useAdmin();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { data, isLoading } = getData(
    ["order", filters, pagination],
    "/order",
    { enabled: true },
    {
      status: filters.status || undefined,
      country: filters.country || undefined,
      user: filters.user || undefined,
      order_id: filters.order_id || undefined,
      payment_method: filters.payment_method || undefined,
      wompi_reference: filters.wompi_reference || undefined,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      start_date: dateRange[0] ? dateRange[0].toISOString().split('T')[0] : undefined,
      end_date: dateRange[1] ? dateRange[1].toISOString().split('T')[0] : undefined,
    }
  );

  const openPaymentProof = (order) => {
    setProofOrder(order);
    setProofModalOpen(true);
  };

  const closePaymentProof = () => {
    setProofModalOpen(false);
    setProofOrder(null);
  };

  const columns = useMemo(
    () => getOrderColumns({ openPaymentProof }),
    [openPaymentProof]
  );

  // `overrides` permite a las píldoras de estado disparar la búsqueda con el
  // valor nuevo sin esperar al siguiente render (closures de useState).
  const handleSearch = (overrides = {}) => {
    const nextStatus = overrides.status !== undefined ? overrides.status : statusFilter;
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    setFilters({
      status: nextStatus,
      country: countryFilter,
      user: userFilter,
      start_date: dateRange[0] ? dateRange[0].toISOString().split('T')[0] : "",
      end_date: dateRange[1] ? dateRange[1].toISOString().split('T')[0] : "",
      payment_method: paymentMethodFilter,
      wompi_reference: wompiReference,
      order_id: orderIdFilter,
    });
  };

  const handleGenerateExcel = () => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;

      if (typeof value === 'object' && value?.value) {
        params.set(key, value.value);
      } else {
        params.set(key, value);
      }
    });

    if (dateRange[0]) {
      params.set('start_date', dateRange[0].toISOString().split('T')[0]);
    }
    if (dateRange[1]) {
      params.set('end_date', dateRange[1].toISOString().split('T')[0]);
    }

    const url = `${API_BASE_URL}/order/report/excel?${params.toString()}`;
    window.open(url, "_blank");
  };

  const orders = data?.data?.orders ?? [];
  const totalOrders = data?.data?.total ?? 0;
  const totalSales = data?.data?.netSales ?? 0;
  const totalBonusPoints = data?.data?.totalBonusPoints ?? 0;
  const totalRoyaltyPoints = data?.data?.totalRoyaltyPoints ?? 0;

  const canSeeStats = ["ADMIN", "ADMIN_ASSISTANT"].includes(admin?.role);

  return (
    <Fragment>
      <PageHeader
          title="Órdenes"
          crumbs={[{ label: "Tienda" }, { label: "Órdenes" }]}
          actions={
            <>
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" onClick={handleGenerateExcel}>
                <i className="ri-file-excel-2-line" /> Excel
              </button>
              <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => navigate('/order/create')}>
                <i className="ri-add-line" /> Crear orden
              </button>
            </>
          }
        />

        {canSeeStats && (
          <div className="mb-3">
            <StatsCards
              totalOrders={totalOrders}
              totalSales={totalSales}
              totalBonusPoints={totalBonusPoints}
              totalRoyaltyPoints={totalRoyaltyPoints}
            />
          </div>
        )}

        <DataTable
          data={orders}
          total={totalOrders}
          pagination={pagination}
          setPagination={setPagination}
          columns={columns}
          progressPending={isLoading}
          minWidth={1320}
          emptyMessage="No hay órdenes para mostrar."
          filters={
            <FormFilterOrder
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              countryFilter={countryFilter}
              setCountryFilter={setCountryFilter}
              userFilter={userFilter}
              setUserFilter={setUserFilter}
              dateRange={dateRange}
              setDateRange={setDateRange}
              handleSearch={handleSearch}
              paymentMethodFilter={paymentMethodFilter}
              setPaymentMethodFilter={setPaymentMethodFilter}
              wompiReference={wompiReference}
              setWompiReference={setWompiReference}
              orderIdFilter={orderIdFilter}
              setOrderIdFilter={setOrderIdFilter}
              totalOrders={totalOrders}
            />
          }
        />

      {proofModalOpen && (
        <PaymentProofModal
          isOpen={proofModalOpen}
          toggle={closePaymentProof}
          order={proofOrder}
        />
      )}
    </Fragment>
  );
};
